import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FileText, Save, Eye, Check } from 'lucide-react';
import {
  fetchEmployeeProfile,
  fetchFormSubmission,
  fetchFormStatus,
  saveDraft,
  submitForm,
  adminSaveDraft,
  adminSubmitForm,
  adminFetchFormSubmission,
  adminFetchEmployeeProfile
} from '../../../services/employeeFormsApi';

const inputClass = 'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

export default function MarriageDeclarationForm() {
  const navigate = useNavigate();
  const { formCode, employeeId } = useParams();
  const [formData, setFormData] = useState({
    employeeName: '',
    employeeCode: '',
    department: '',
    designation: '',
    dateOfBirth: '',
    dateOfAppointment: '',
    dateOfRetirement: '',
    permanentAddress: '',
    maritalStatus: '',
    exemptionEnclosed: '',
    place: '',
    declarationDate: '',
    declarationAccepted: false
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const code = formCode || 'MARR_DECL';
        let submissionData = null;
        let profileData = null;

        if (employeeId) {
            try {
                const submission = await adminFetchFormSubmission(employeeId, code);
                if (submission && submission.form_data) {
                    submissionData = submission.form_data;
                }
            } catch (e) {
                console.log('No admin draft found');
            }

            try {
                profileData = await adminFetchEmployeeProfile(employeeId);
            } catch (e) {
                console.log('Failed to fetch profile in admin mode');
            }
        } else {
            try {
              const status = await fetchFormStatus(code);
              if (status.status === 'SUBMITTED' || status.status === 'APPROVED') {
                navigate(`/employee/forms/${code}/preview`);
                return;
              }
            } catch (error) {
              console.log('No form status found');
            }

            try {
              const submission = await fetchFormSubmission(code);
              if (submission && submission.form_data) {
                submissionData = submission.form_data;
              }
            } catch (error) {
              console.log('No existing draft found');
            }

            try {
              profileData = await fetchEmployeeProfile();
            } catch (error) {
              console.log('Failed to fetch profile');
            }
        }

        setFormData(prev => {
            let newData = { ...prev };
            if (submissionData) {
                newData = { ...newData, ...submissionData };
            }
            if (profileData) {
                newData.employeeName = profileData.full_name || '';
                newData.employeeCode = profileData.emp_code || '';
                newData.department = profileData.department || '';
                newData.designation = profileData.designation || '';
                newData.dateOfBirth = profileData.date_of_birth ? profileData.date_of_birth.split('T')[0] : '';
                newData.dateOfAppointment = profileData.date_of_joining ? profileData.date_of_joining.split('T')[0] : '';
                newData.dateOfRetirement = profileData.date_of_retirement ? profileData.date_of_retirement.split('T')[0] : '';
                newData.permanentAddress = profileData.permanent_address || profileData.current_address || '';
            }
            return newData;
        });

      } catch (error) {
        console.error('Error loading initial data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [formCode, navigate]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.maritalStatus) {
      newErrors.maritalStatus = 'Marital status is required';
    }

    if (formData.maritalStatus === 'married_while_spouse_living' && !formData.exemptionEnclosed) {
      newErrors.exemptionEnclosed = 'Please specify if exemption application is enclosed';
    }

    if (!formData.place.trim()) {
      newErrors.place = 'Place is required';
    }

    if (!formData.declarationDate) {
      newErrors.declarationDate = 'Declaration date is required';
    }

    if (!formData.declarationAccepted) {
      newErrors.declaration = 'Please accept the declaration to submit';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    setSaveSuccess(false);
    try {
      const code = formCode || 'MARR_DECL';
      if (employeeId) {
          await adminSaveDraft(employeeId, code, formData);
      } else {
          await saveDraft(code, formData);
      }
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving draft:', error);
      alert('Failed to save draft: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    if (validateForm()) {
      await handleSaveDraft();
      const code = formCode || 'MARR_DECL';
      navigate(`/employee/forms/${code}/preview`, { state: formData });
    }
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      if (!formData.declarationAccepted) {
        setErrors(prev => ({
          ...prev,
          declaration: 'Please accept the declaration to submit'
        }));
        return;
      }
      try {
        const code = formCode || 'MARR_DECL';
        if (employeeId) {
            await adminSubmitForm(employeeId, code, formData);
            navigate(`/employees/${employeeId}/profile?tab=documents`, { replace: true });
        } else {
            await submitForm(code, formData);
            navigate(`/user-dashboard?tab=my-forms`, { replace: true });
        }
      } catch (error) {
          alert('Failed to submit form: ' + error.message);
      }
    }
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">Loading form...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="h-12 w-12 rounded-lg bg-blue-50 flex items-center justify-center">
            <FileText className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Marriage Declaration Form</h1>
            <p className="text-gray-600">Decision No. 2 under Rule 21 of Central Civil Services (Conduct) Rules, 1964</p>
          </div>
        </div>
      </div>

      {/* Section 1: Employee Details */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Employee Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Employee Name</label>
            <input
              type="text"
              value={formData.employeeName}
              className={`${inputClass} bg-gray-50`}
              readOnly
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Employee Code</label>
            <input
              type="text"
              value={formData.employeeCode}
              className={`${inputClass} bg-gray-50`}
              readOnly
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
            <input
              type="text"
              value={formData.department}
              className={`${inputClass} bg-gray-50`}
              readOnly
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Designation</label>
            <input
              type="text"
              value={formData.designation}
              className={`${inputClass} bg-gray-50`}
              readOnly
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
            <input
              type="date"
              value={formData.dateOfBirth}
              className={`${inputClass} bg-gray-50`}
              readOnly
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date of Appointment</label>
            <input
              type="date"
              value={formData.dateOfAppointment}
              className={`${inputClass} bg-gray-50`}
              readOnly
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date of Retirement</label>
            <input
              type="date"
              value={formData.dateOfRetirement}
              className={`${inputClass} bg-gray-50`}
              readOnly
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Permanent Address</label>
            <textarea
              value={formData.permanentAddress}
              rows={3}
              className={`${inputClass} bg-gray-50`}
              readOnly
            />
          </div>
        </div>
      </div>

      {/* Section 2: Marital Status Declaration */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Marital Status Declaration</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Marital Status <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.maritalStatus}
              onChange={(e) => handleInputChange('maritalStatus', e.target.value)}
              className={`${inputClass} ${errors.maritalStatus ? 'border-red-300 bg-red-50' : ''}`}
            >
              <option value="">Select marital status</option>
              <option value="unmarried">Unmarried</option>
              <option value="widower">Widower</option>
              <option value="widow">Widow</option>
              <option value="married_one_spouse">Married and only one spouse living</option>
              <option value="married_while_spouse_living">Married while spouse living</option>
            </select>
            {errors.maritalStatus && (
              <p className="text-red-600 text-sm mt-1">{errors.maritalStatus}</p>
            )}
          </div>

          {formData.maritalStatus === 'married_while_spouse_living' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Application for exemption enclosed <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="yes"
                    checked={formData.exemptionEnclosed === 'yes'}
                    onChange={(e) => handleInputChange('exemptionEnclosed', e.target.value)}
                    className="mr-2"
                  />
                  Yes
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="no"
                    checked={formData.exemptionEnclosed === 'no'}
                    onChange={(e) => handleInputChange('exemptionEnclosed', e.target.value)}
                    className="mr-2"
                  />
                  No
                </label>
              </div>
              {errors.exemptionEnclosed && (
                <p className="text-red-600 text-sm mt-1">{errors.exemptionEnclosed}</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Section 3: Declaration Details */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Declaration Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Place <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.place}
              onChange={(e) => handleInputChange('place', e.target.value)}
              placeholder="Enter place"
              className={`${inputClass} ${errors.place ? 'border-red-300 bg-red-50' : ''}`}
            />
            {errors.place && (
              <p className="text-red-600 text-sm mt-1">{errors.place}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Declaration Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={formData.declarationDate}
              onChange={(e) => handleInputChange('declarationDate', e.target.value)}
              className={`${inputClass} ${errors.declarationDate ? 'border-red-300 bg-red-50' : ''}`}
            />
            {errors.declarationDate && (
              <p className="text-red-600 text-sm mt-1">{errors.declarationDate}</p>
            )}
          </div>
        </div>
      </div>

      {/* Section 4: Declaration Acceptance */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.declarationAccepted}
            onChange={(e) => handleInputChange('declarationAccepted', e.target.checked)}
            className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">
            I hereby declare that the information provided above is true and correct to the best of my knowledge and belief. I understand that any false statement may lead to disciplinary action.
          </span>
        </label>
        {errors.declaration && (
          <p className="text-red-600 text-sm mt-2">{errors.declaration}</p>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-3 mt-8 border-t border-gray-200 pt-6">
        <button
          type="button"
          onClick={() => employeeId ? navigate(`/employees/${employeeId}/profile?tab=documents`) : navigate(-1)}
          className="inline-flex items-center gap-2 px-6 py-2.5 border border-gray-300 bg-white text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSaveDraft}
          disabled={saving}
          className="inline-flex items-center gap-2 px-6 py-2.5 border border-gray-300 bg-white text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving...' : 'Save Draft'}
        </button>
        {saveSuccess && (
          <span className="text-green-600 text-sm">Draft saved!</span>
        )}
        {!employeeId && (
          <button
            type="button"
            onClick={handlePreview}
            className="inline-flex items-center gap-2 px-6 py-2.5 border border-gray-300 bg-white text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            <Eye className="w-4 h-4" />
            Preview
          </button>
        )}
        {employeeId && (
          <button
            type="button"
            onClick={handleSubmit}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Check className="w-4 h-4" />
            Save / Submit
          </button>
        )}
      </div>
    </div>
  );
}
